import React, { useMemo } from 'react';
import { ISummaryBlock, IDossier, ITitleBlock, ISection, IChapter } from './types';
import ChevronRight from '../../icons/ChevronRight';
import { useDossier } from '../../dossierEditor/DossierContext';

interface SummaryWidgetProps {
    block: ISummaryBlock;
    dossierContent: IDossier | null;
    currentChapterIndex: number;
    onChange: (newBlock: ISummaryBlock) => void;
    mode: 'editor' | 'reader';
}

interface SummaryItem {
    id: string;
    type: 'chapter' | 'title';
    content: string;
    level: number;
    chapterIndex: number;
    blockId?: string;
}

const SummaryWidget: React.FC<SummaryWidgetProps> = ({
    block,
    dossierContent,
    onChange,
    mode
}) => {
    const { currentChapterIndex, setCurrentChapterIndex } = useDossier();

    // Extraire la structure hiérarchique (Chapitres + Titres)
    const items = useMemo<SummaryItem[]>(() => {
        if (!dossierContent) return [];

        const result: SummaryItem[] = [];

        dossierContent.content.forEach((chapter: IChapter, cIndex: number) => {
            // Filtrage par portée (Scope)
            if (block.scope === 'chapter' && cIndex !== currentChapterIndex) {
                return;
            }

            // 1. Ajouter le Chapitre comme élément racine (Niveau 0)
            // Uniquement si on est en mode 'dossier', ou pour donner le contexte en mode 'chapter'
            result.push({
                id: chapter._id,
                type: 'chapter',
                content: chapter.title,
                level: 0,
                chapterIndex: cIndex
            });

            // 2. Parcourir les sections et blocs pour trouver les titres.
            //    `?? []` : tolérance aux dossiers historiques sans champ blocks.
            (chapter.sections ?? []).forEach((section: ISection) => {
                (section.blocks ?? []).forEach((b: any) => {
                    if (b.type === 'title') {
                        const titleBlock = b as ITitleBlock;
                        // Filtrer les titres exclus du sommaire
                        if (titleBlock.showInSummary === false) return;

                        result.push({
                            id: titleBlock._id,
                            type: 'title',
                            content: titleBlock.content,
                            level: titleBlock.level || 1, // H1 = level 1
                            chapterIndex: cIndex,
                            blockId: titleBlock._id
                        });
                    }
                });
            });
        });

        return result;
    }, [dossierContent, currentChapterIndex, block.scope]);

    const handleScopeChange = (newScope: 'dossier' | 'chapter') => {
        onChange({ ...block, scope: newScope });
    };

    const [showControls, setShowControls] = React.useState(false);

    const handleItemClick = (item: SummaryItem) => {
        // 1. Changer de chapitre si nécessaire
        if (item.chapterIndex !== currentChapterIndex) {
            setCurrentChapterIndex(item.chapterIndex);
        }

        // 2. Scroller vers l'élément (après un court délai pour laisser le temps au rendu)
        if (item.type === 'title' && item.blockId) {
            setTimeout(() => {
                const element = document.querySelector(`[data-block-id="${item.blockId}"]`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
        } else if (item.type === 'chapter') {
            // Pour un chapitre, on scroll juste en haut
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <div
            className="relative w-full my-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-mcdm-third-400"
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
        >
            {/* Header minimaliste avec juste les toggles (visible uniquement en éditeur et au survol) */}
            {mode === 'editor' && showControls && (
                <div className="absolute top-0 right-2 z-10">
                    <div className="flex gap-1 text-xs bg-gray-200 dark:bg-mcdm-third-500 rounded-b p-1">
                        <button
                            onClick={() => handleScopeChange('chapter')}
                            className={`px-2 py-1 rounded transition-colors ${block.scope === 'chapter'
                                ? 'bg-white shadow text-mcdm-third-500 font-medium'
                                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                                }`}
                        >
                            Chapitre
                        </button>
                        <button
                            onClick={() => handleScopeChange('dossier')}
                            className={`px-2 py-1 rounded transition-colors ${block.scope === 'dossier'
                                ? 'bg-white shadow text-mcdm-third-500 font-medium'
                                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                                }`}
                        >
                            Dossier
                        </button>
                    </div>
                </div>
            )}

            <div className="flex flex-col gap-1">
                {items.length === 0 ? (
                    <p className="text-gray-500 italic text-sm p-2">Aucun sommaire disponible.</p>
                ) : (
                    items.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => handleItemClick(item)}
                            className={`
                                text-left transition-colors group flex items-start py-1 rounded
                                ${item.type === 'chapter'
                                    ? 'hover:bg-gray-100 dark:hover:bg-mcdm-third-300 mt-2 mb-1 pl-1'
                                    : 'hover:text-mcdm-secondary-500 dark:hover:text-mcdm-secondary-400'
                                }
                            `}
                            style={{
                                // Indentation : 0 pour Chapitre, (TitleLevel) * 20px pour les titres
                                // On ajoute un peu de retrait global pour les titres (Niveau 0 = Chapitre)
                                paddingLeft: item.type === 'chapter' ? '4px' : `${(item.level) * 15}px`
                            }}
                        >
                            {item.type === 'title' && (
                                <span className="mt-[5px] mr-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                    <ChevronRight width="10" height="10" />
                                </span>
                            )}

                            <span className={`
                                ${item.type === 'chapter'
                                    ? 'font-bold font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100 text-base border-l-4 border-mcdm-secondary-200 pl-2'
                                    : ''
                                }
                                ${item.type === 'title' && item.level === 1 ? 'font-semibold font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100' : ''}
                                ${item.type === 'title' && item.level > 1 ? 'text-mcdm-third-400 dark:text-mcdm-primary-200 text-sm' : ''}
                            `}>
                                {item.content || '(Sans titre)'}
                            </span>
                        </button>
                    ))
                )}
            </div>
        </div>
    );
};

export default SummaryWidget;
